import { NotificationDefinition, NotificationSeverity } from '@cast/types';

/**
 * One notification catalog definition as shown in the UI. The data comes
 * from GET /v1/notifications/definitions (the server-owned notification
 * catalog, WIRE-2553) — nothing here is maintained by hand.
 */
export type CatalogueLabel = {
  id: string;
  displayName: string;
};

export type CatalogueItem = {
  /** Definition key, e.g. "cluster_reconcile_failed". */
  id: string;
  name: string;
  description: string;
  /** Message template with `{{.labels.x}}` placeholders. */
  template: string;
  domain: string;
  /** Severity string from the API contract (e.g. "warning"). */
  severity: string;
  labels: CatalogueLabel[];
};

/** Maps the API definition contract to the catalogue view model. */
export const definitionToCatalogueItem = (
  definition: NotificationDefinition
): CatalogueItem => ({
  id: definition.key ?? '',
  name: definition.displayName ?? '',
  description: definition.description ?? '',
  template: definition.message ?? '',
  domain: definition.domain ?? '',
  severity: definition.severity ?? '',
  labels: (definition.labels ?? []).map(({ id, displayName }) => ({
    id: id ?? '',
    displayName: displayName ?? '',
  })),
});

/**
 * Best-effort cast of an API severity string to the NotificationSeverity
 * enum used by the StatusChipV2-based SeverityChip. Returns '' for
 * unknown/unmatched severities — callers render raw text as a fallback.
 * Case-insensitive: the catalog returns lowercase ("warning") while the
 * legacy enum values are uppercase ("WARNING").
 */
export const toNotificationSeverity = (
  severity: string
): NotificationSeverity | '' => {
  for (const value of Object.values(NotificationSeverity)) {
    if (value.toLowerCase() === severity.toLowerCase()) {
      return value;
    }
  }
  return '';
};

/**
 * Renders a message template for display: `{{.labels.cluster_name}}`
 * placeholders (Go text/template style from the catalog contract; the
 * dotless `{{labels.x}}` spelling is also accepted) become human-readable
 * text like "the cluster name".
 */
export const interpolateTemplate = (
  template: string,
  placeholderPrefix = 'the'
): string =>
  template.replace(
    /\{\{\s*\.?\s*labels\.([a-zA-Z0-9_]+)\s*\}\}/g,
    (_, label: string) =>
      `${placeholderPrefix} ${label.replace(/_/g, ' ').toLowerCase()}`
  );
